import { useEffect, useMemo, useState } from 'react';
import { Handle, Position, useNodeId } from 'reactflow';
import * as Icons from "@mui/icons-material";

import './flow-node.css'
import { Port, NodeLoadStateEvent } from '../../peregrineFlowInterfaces';
import { LinearProgress, Tooltip } from '@mui/material';
import { HelpOutline } from '@mui/icons-material';

import useStore, { CustomReactFlowNode } from '../../store';
import { socket } from '../../socket';

export type IconNames = keyof typeof Icons // use this in other components

interface Indexable {
    [key: string]: any;
}

// colors were generated using https://medialab.github.io/iwanthue/
// Params H: 0 - 360, C: 0 - 100, L: 0 - 45
// if you select a group, just use a color which is not assigned yet.
export const groupColors: Indexable = {
    "io": "#005500",
    "cv": "#b82e22",
    "draw": "#6239ca",
    "eval": "#2a6525",
    "util": "#982d9f",
    "display": "#526024",
    "nn": "#482e91",
    // "#765424",
    // "#51569c",
    // "#8b3622",
    // "#392259",
    // "#3c6255",
    // "#ac2b56",
    // "#2f371e",
    // "#863371",
    // "#192f38",
    // "#641f36",
    // "#321b2f",
    // "#7b5056",
    // "#50281c"
}

export const parseDataTypes = (data_type: any) => {
    const type = typeof data_type;

    if (data_type instanceof String) {
        return type;
    } else if (data_type instanceof Array) {
        return data_type.map((item: string) => {
            const type_id = `type-${item}`;
            return (
                <div key={type_id}>{item}</div>
            )
        });
    }

    return data_type;
}

const FlowNode = (data: any, isConnectable: boolean) => {
    const node = (data as CustomReactFlowNode).data;
    const [inputPorts, setInputPorts] = useState<Array<Port>>([]);
    const [outputPorts, setOutputPorts] = useState<Array<Port>>([]);
    const [nodeLoadState, setNodeLoadState] = useState<NodeLoadStateEvent>();
    const edges = useStore((state) => state.edges);


    useEffect(() => {
        setInputPorts(node.input_ports);
        setOutputPorts(node.output_ports);

        const onLoadState = (state: NodeLoadStateEvent) => {
            if (state.node.id !== node.id) {
                return;
            }

            setNodeLoadState(state);
        }

        socket.on('node.state', onLoadState);
        return () => {
            socket.off('node.state', onLoadState);
        };
    }, [node.id, node.input_ports, node.output_ports])

    const nodeId = useNodeId();
    const color = useMemo(() => {
        return node.group in groupColors ? groupColors[node.group] : "var(--blue-one)";
    }, [node.group])

    const renderProgress = useMemo(() => {
        if (!nodeLoadState || nodeLoadState?.message_type === 1) {
            return ""
        }

        if (nodeLoadState.data?.length_computable) {
            const value = (nodeLoadState.data.loaded! / nodeLoadState.data.total!) * 100;
            return <LinearProgress value={value} variant='determinate' sx={{ m: 2 }} />
        }

        return <LinearProgress sx={{ m: 2 }} />
    }, [nodeLoadState]);

    const inputHandles = useMemo(() => {
        if (inputPorts.length === 0) {
            return '';
        }

        const items = inputPorts.map((port: Port, i: number) => {
            const handleId = `input-${nodeId}-port-id-${i}`;
            const isConnected = edges.some(edge => edge.targetHandle === handleId )
            return (
                <div key={handleId} className={`port ${isConnected ? 'connected' : 'not-connected'}`}>
                    <Handle
                        type="target"
                        position={Position.Left}
                        id={handleId}
                        isConnectable={true}
                    />
                    <div className='description'>
                        <div className='label'>{port.label}</div>
                        <div className='type'>{parseDataTypes(port.data_type)}</div>
                    </div>
                </div>
            );
        });

        return (
            <div className='input ports'>
                {items}
            </div>
        )
    }, [inputPorts, nodeId, edges]);

    const outputHandles = useMemo(() => {
        const items = outputPorts.map((port: Port, i: number) => {
            const handleId = `output-${nodeId}-port-id-${i}`;
            const isConnected = edges.some(edge => edge.sourceHandle === handleId )
            let helpMessage = <></>

            if (port.description !== "" && port.description !== undefined) {
                helpMessage = (<Tooltip title={port.description}>
                    <HelpOutline style={{ fontSize: "8pt" }} />
                </Tooltip>)
            }


            return (
                <div key={handleId} className={`port ${isConnected ? 'connected' : 'not-connected'}`}>
                    <Handle
                        key={handleId}
                        type="source"
                        position={Position.Right}
                        id={handleId}
                    />
                    <div className='description'>
                        <div className='label'>{port.label} {helpMessage}</div>
                        <div className='type'>{parseDataTypes(port.data_type)}</div>
                    </div>
                </div>
            );
        })

        return (
            <div className='output ports'>
                {items}
            </div>
        )
    }, [outputPorts, nodeId, edges]);

    const footer = useMemo(() => {
        if (node.icon === null) {
            return (
                <footer style={{ background: color }}></footer>
            )
        }

        const iconName = node.icon as IconNames;
        const Icon = Icons[iconName];
        return (
            <footer style={{ background: color }}>
                <Tooltip title={node.description} style={{ background: color }}>
                    <Icon className='icon' />
                </Tooltip>
            </footer>
        )

    }, [node.icon, node.description, color]);

    return (
        <div className="flow-node">
            <header style={{ background: color }}>
                {node.display_name}
            </header>
            <div>
                {inputHandles}
                {outputHandles}
                {renderProgress}
            </div>
            {footer}
        </div>
    );
};

export default FlowNode;
